import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';
import * as Sentry from '@sentry/react';

/**
 * Error Boundary Component
 * Catches JavaScript errors in child component tree and displays fallback UI
 * Integrates with Sentry for error tracking
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      eventId: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    
    // Send error to Sentry
    const eventId = Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo?.componentStack,
        props: this.props,
      },
      tags: {
        errorBoundary: this.props.boundaryName || 'unknown',
      },
    });
    
    this.setState({ eventId });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, eventId: null });
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  handleReportFeedback = () => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div 
          className="min-h-[400px] flex items-center justify-center p-6"
          role="alert"
          aria-live="assertive"
        >
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" aria-hidden="true" />
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Something went wrong
            </h2>
            
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {this.props.message || "We're sorry, but something unexpected happened. Please try again."}
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mb-4 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 dark:bg-gray-900 rounded text-xs overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="flex gap-3 justify-center flex-wrap">
              <Button
                onClick={this.handleRetry}
                variant="outline"
                className="flex items-center gap-2"
                aria-label="Try again"
              >
                <RefreshCw className="w-4 h-4" aria-hidden="true" />
                Try Again
              </Button>
              
              <Button
                onClick={this.handleGoHome}
                className="flex items-center gap-2"
                aria-label="Go to dashboard"
              >
                <Home className="w-4 h-4" aria-hidden="true" />
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Page-level Error Boundary with specific styling
 */
export const PageErrorBoundary = ({ children, pageName }) => (
  <ErrorBoundary 
    message={`There was a problem loading the ${pageName || 'page'}. Please try refreshing.`}
    boundaryName={`page-${pageName || 'unknown'}`}
  >
    {children}
  </ErrorBoundary>
);

/**
 * Component-level Error Boundary (smaller, inline)
 */
export const ComponentErrorBoundary = ({ children, componentName }) => (
  <ErrorBoundary
    boundaryName={`component-${componentName || 'unknown'}`}
    fallback={
      <div 
        className="p-4 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20"
        role="alert"
      >
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
          <AlertTriangle className="w-4 h-4" aria-hidden="true" />
          <span className="text-sm font-medium">
            Failed to load {componentName || 'component'}
          </span>
        </div>
      </div>
    }
  >
    {children}
  </ErrorBoundary>
);

export default ErrorBoundary;
